import React__default from 'react';
import { getId } from './ScrollableList.js';
import { setInputValueByRef as setInputValueByRef$1 } from '../../utils/input.js';

const getValue = value => String(value !== null && value !== void 0 ? value : '');

const setInputValueByRef = (input, value, event = 'change') => {
  setInputValueByRef$1(input, getValue(value), event);
};
const getIndexFromValue = (data, value) => {
  const index = data.findIndex(option => getValue(option.value) === getValue(value));
  return index > -1 ? index : undefined;
};
const getSelectedIndexesFromValue = (data, value) => {
  if (typeof value !== 'string') return [];
  return value.split(',').map(v => data.findIndex(o => String(o.value) === v)).filter(v => v !== -1);
};
const findByValue = (data, value) => {
  return data.find(option => getValue(option.value) === getValue(value));
};
const searchForString = (child, value, strategy = 'includes') => {
  try {
    var _child$props;

    if (typeof child !== 'string' && (_child$props = child.props) !== null && _child$props !== void 0 && _child$props.children) {
      var _child$props2, _child$props3;

      if (Array.isArray((_child$props2 = child.props) === null || _child$props2 === void 0 ? void 0 : _child$props2.children)) {
        return !!child.props.children.find(subChild => searchForString(subChild, value, strategy));
      }

      return searchForString((_child$props3 = child.props) === null || _child$props3 === void 0 ? void 0 : _child$props3.children, value, strategy);
    } else {
      return child.toString().toLowerCase()[strategy](String(value).toLowerCase());
    }
  } catch {
    return false;
  }
};

const getIndexInRangeByValue = (data, query, firstIndex, lastIndex = data.length) => {
  const index = data.slice(firstIndex, lastIndex).findIndex(option => {
    if (option.disabled) {
      return false;
    }

    return searchForString(option.text, query, 'startsWith');
  });
  return index > -1 ? index + firstIndex : -1;
};

const findNextIndexByValue = (data, query, activeIndex) => {
  const firstIndex = activeIndex === undefined ? 0 : activeIndex + 1;
  let index = getIndexInRangeByValue(data, query, firstIndex);

  if (index === -1) {
    index = getIndexInRangeByValue(data, query, 0, activeIndex);
  }

  return index;
};
const getActiveDescendant = (data, currentIndex, id) => {
  return currentIndex !== undefined && data[currentIndex] ? getId(id, data[currentIndex].value) : undefined;
};
const useFlattenedData = data => {
  return React__default.useMemo(() => {
    const items = [];

    const flatten = (item, path, parentPath, parentDisabled) => {
      const currentPath = parentPath ? `${parentPath}.${path}` : `${path}`;
      const isItemDisabled = parentDisabled || item.disabled;
      items.push({ ...item,
        disabled: isItemDisabled,
        path: currentPath,
        hasChildren: !!item.children
      });

      if (item.children) {
        item.children.forEach((childItem, childPath) => flatten(childItem, childPath, currentPath, isItemDisabled));
      }
    };

    data.forEach((item, index) => {
      flatten(item, index);
    });
    return items;
  }, [data]);
};
const sanitizeItem = item => {
  if (item) {
    const {
      hasChildren: _1,
      path: _2,
      ...rest
    } = item;
    return rest;
  }

  return undefined;
};
const getOptionParents = (data, path) => {
  var _path$split;

  const indexes = (_path$split = path === null || path === void 0 ? void 0 : path.split('.')) !== null && _path$split !== void 0 ? _path$split : [];

  if (indexes.length > 1) {
    // we don't want to map the current item
    indexes.pop(); // we need to rebuild the path as we map

    let lastPath;
    return indexes.map(i => {
      lastPath = lastPath ? [lastPath, i].join('.') : i;
      return sanitizeItem(data.find(i => i.path === lastPath));
    });
  }

  return null;
};
const filterData = (data, value = '') => {
  if (value === '' || value === null) {
    return data;
  }

  const filteredOptionValues = new Set();

  const shouldFilterOption = option => {
    if (option.disabled) {
      return false;
    }

    return searchForString(option.text, String(value));
  };

  data.forEach(option => {
    if (shouldFilterOption(option)) {
      const parents = getOptionParents(data, option.path);
      filteredOptionValues.add(option.value);

      if (parents !== null) {
        parents.forEach(option => filteredOptionValues.add(option === null || option === void 0 ? void 0 : option.value));
      }
    }
  });
  return data.filter(({
    value
  }) => filteredOptionValues.has(value));
};

export { filterData, findByValue, findNextIndexByValue, getActiveDescendant, getIndexFromValue, getOptionParents, getSelectedIndexesFromValue, sanitizeItem, searchForString, setInputValueByRef, useFlattenedData };
//# sourceMappingURL=util.js.map
